import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack
} from "@mui/material";
import { EthiopianDatePicker } from "components/UI";
import { Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BsPlus, BsTrash } from "react-icons/bs";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${({ theme, error }) =>
    error ? theme.palette.error.main : "inherit"};
`;

export const FamilyInfoForm = ({
  register,
  errors,
  control,
  watch,
  setValue
}) => {
  const { t } = useTranslation();

  const {
    fields: children,
    append: appendChild,
    remove: removeChild
  } = useFieldArray({
    control,
    name: "children"
  });

  return (
    <Stack spacing={2}>
      <FormControl>
        <FormLabel sx={{ fontSize: "12px" }}>
          {t("HR.PersonalInfo.MaritalStatus")}
        </FormLabel>
        <Controller
          control={control}
          name="marital_status"
          render={({ field }) => (
            <RadioGroup row {...field}>
              <FormControlLabel
                value="SINGLE"
                control={<Radio size="small" />}
                label={t("addMembers.step3.Single")}
              />
              <FormControlLabel
                value="MARRIED"
                control={<Radio size="small" />}
                label={t("addMembers.step3.Married")}
              />
              <FormControlLabel
                value="DIVORCED"
                control={<Radio size="small" />}
                label={t("addMembers.step3.Divorced")}
              />
              <FormControlLabel
                value="WIDOWED"
                control={<Radio size="small" />}
                label={t("addMembers.step3.Widowed")}
              />
            </RadioGroup>
          )}
        />
      </FormControl>
      {watch("marital_status") === "MARRIED" && (
        <>
          <Field>
            <Label htmlFor="date_married">
              {t("HR.FamilyInformation.DateMarried")}
            </Label>
            <EthiopianDatePicker
              value={watch("date_married")}
              onChange={e => setValue("date_married", e.target.value)}
            />
            <ErrorMessage
              errors={errors}
              name="date_married"
              render={({ message }) => <HelperText error>{message}</HelperText>}
            />
          </Field>
          <Field>
            <Label htmlFor="spouse_name">
              {t("HR.FamilyInformation.SpouseName")}
            </Label>
            <Input id="spouse_name" {...register("spouse_name")} />
            <ErrorMessage
              errors={errors}
              name="spouse_name"
              render={({ message }) => <HelperText error>{message}</HelperText>}
            />
          </Field>
          <Field>
            <Label htmlFor="spouse_phone_number">
              {t("HR.FamilyInformation.SpousePhoneNumber")}
            </Label>
            <Input
              id="spouse_phone_number"
              {...register("spouse_phone_number")}
            />
            <ErrorMessage
              errors={errors}
              name="spouse_phone_number"
              render={({ message }) => <HelperText error>{message}</HelperText>}
            />
          </Field>
          <Field>
            <Label htmlFor="spouse_job_title">
              {t("HR.FamilyInformation.SpouseJobTitle")}
            </Label>
            <Input id="spouse_job_title" {...register("spouse_job_title")} />
            <ErrorMessage
              errors={errors}
              name="spouse_job_title"
              render={({ message }) => <HelperText error>{message}</HelperText>}
            />
          </Field>
        </>
      )}
      <FormControl>
        <FormLabel sx={{ fontSize: "12px" }}>
          {t("HR.FamilyInformation.HaveChildren")}
        </FormLabel>
        <Controller
          control={control}
          name="has_children"
          render={({ field }) => (
            <RadioGroup row {...field}>
              <FormControlLabel
                value={true}
                control={<Radio size="small" />}
                label={t("form.common.yes")}
              />
              <FormControlLabel
                value={false}
                control={<Radio size="small" />}
                label={t("form.common.no")}
              />
            </RadioGroup>
          )}
        />
      </FormControl>
      {watch("has_children") === "true" && (
        <Stack spacing={2}>
          {children.map((child, index) => (
            <Box
              key={child.id}
              sx={{
                position: "relative",
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1
              }}
            >
              <IconButton
                size="small"
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => removeChild(index)}
              >
                <BsTrash />
              </IconButton>
              <Stack spacing={2}>
                <Field>
                  <Label>{t("HR.FamilyInformation.ChildName")}</Label>
                  <Input {...register(`children.${index}.full_name`)} />
                  <ErrorMessage
                    errors={errors}
                    name={`children.${index}.full_name`}
                    render={({ message }) => (
                      <HelperText error>{message}</HelperText>
                    )}
                  />
                </Field>
                <Field>
                  <Label>{t("HR.FamilyInformation.ChildBirthDate")}</Label>
                  <EthiopianDatePicker
                    value={watch(`children.${index}.date_of_birth`)}
                    onChange={e =>
                      setValue(
                        `children.${index}.date_of_birth`,
                        e.target.value
                      )
                    }
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`children.${index}.date_of_birth`}
                    render={({ message }) => (
                      <HelperText error>{message}</HelperText>
                    )}
                  />
                </Field>
                <Field>
                  <Label>{t("HR.FamilyInformation.ChildGender")}</Label>
                  <Controller
                    control={control}
                    name={`children.${index}.gender`}
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="MALE"
                          control={<Radio size="small" />}
                          label={t("form.common.male")}
                        />
                        <FormControlLabel
                          value="FEMALE"
                          control={<Radio size="small" />}
                          label={t("form.common.female")}
                        />
                      </RadioGroup>
                    )}
                  />
                </Field>
              </Stack>
            </Box>
          ))}
          <Button
            type="button"
            onClick={() =>
              appendChild({
                full_name: "",
                date_of_birth: null,
                gender: ""
              })
            }
            startIcon={<BsPlus />}
            variant="outlined"
            fullWidth
          >
            {t("HR.FamilyInformation.AddChild")}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
